import { IDBPDatabase, IDBPObjectStore, IDBPTransaction, openDB } from "idb";
import { Photo, SyncStatus } from "../types/Photo";
import {
  assignProductIds,
  purgeableProducts,
  RETENTION_DAYS,
} from "../utils/product";
import { messageOf } from "../utils/errorMessage";

const DB_NAME = "tpp-tracabilite-db";
const STORE_NAME = "photos";
const DB_VERSION = 5; // Incremented for schema change

/**
 * Le nom que `productId` portait jusqu'en v3, quand le regroupement des photos
 * d'un produit s'appelait encore autrement. Seule la migration le connaît.
 */
const LEGACY_PRODUCT_KEY = "albumId";

type UpgradeStore = IDBPObjectStore<
  unknown,
  ArrayLike<string>,
  string,
  "versionchange"
>;

/**
 * Rattache à un produit les photos déjà présentes sur l'appareil.
 *
 * Les dates seules sont lues d'abord : charger d'un coup les blobs de toute la
 * file ferait tenir plusieurs centaines de mégaoctets en mémoire sur le
 * téléphone. Les photos sont ensuite réécrites une par une.
 */
async function backfillProductIds(store: UpgradeStore): Promise<void> {
  const toBackfill: { id: IDBValidKey; createdAt: Date; productId?: string }[] =
    [];
  let cursor = await store.index("createdAt").openKeyCursor();
  while (cursor) {
    toBackfill.push({ id: cursor.primaryKey, createdAt: cursor.key as Date });
    cursor = await cursor.continue();
  }

  for (const { id, productId } of assignProductIds(toBackfill)) {
    const photo = (await store.get(id)) as Photo | undefined;
    if (photo && !photo.productId) {
      photo.productId = productId!;
      await store.put(photo);
    }
  }
}

/**
 * Recopie le champ d'hier dans `productId`, et rien de plus.
 *
 * Surtout ne rien redéduire ici : `backfillProductIds` nomme un produit par
 * journée, ce qui est juste pour des photos qui n'en portent aucun, et
 * destructeur pour celles qui en portent un — les produits des sept derniers
 * jours fondraient en un par jour, sans qu'aucune erreur ne le dise.
 *
 * Le curseur ne tient qu'une photo à la fois : les blobs ne s'accumulent pas.
 */
async function renameProductKey(store: UpgradeStore): Promise<void> {
  let cursor = await store.openCursor();
  while (cursor) {
    const photo = cursor.value as Record<string, unknown>;
    const legacy = photo[LEGACY_PRODUCT_KEY];
    if (typeof legacy === "string") {
      photo.productId = legacy;
      delete photo[LEGACY_PRODUCT_KEY];
      await cursor.update(photo);
    }
    cursor = await cursor.continue();
  }
}

/**
 * Retire `isSelected` de chaque photo.
 *
 * Le champ portait un tri avant envoi qui n'existe plus : tout ce qui est pris
 * pour un produit part avec lui. Une photo décochée n'était pas destinée à
 * disparaître, seulement à ne pas partir — la migration n'en supprime donc
 * aucune, elle ne fait qu'oublier la coche.
 *
 * Le curseur ne tient qu'une photo à la fois : les blobs ne s'accumulent pas.
 */
async function dropSelectionFlag(store: UpgradeStore): Promise<void> {
  let cursor = await store.openCursor();
  while (cursor) {
    const photo = cursor.value as Record<string, unknown>;
    if ("isSelected" in photo) {
      delete photo.isSelected;
      await cursor.update(photo);
    }
    cursor = await cursor.continue();
  }
}

/**
 * Les migrations du magasin `photos`, dans la transaction `versionchange` que
 * `openDB` fournit.
 *
 * Tout est attendu sur place. Une migration lancée sans `await` se détache :
 * la transaction se referme dès qu'aucune requête ne la tient, l'ordre des
 * montées de version n'est plus garanti, et une erreur y est avalée au lieu
 * d'empêcher la version d'être écrite.
 */
export const upgradePhotoStore = async (
  db: IDBPDatabase<unknown>,
  oldVersion: number,
  _newVersion: number | null,
  transaction: IDBPTransaction<unknown, ArrayLike<string>, "versionchange">
): Promise<void> => {
  if (!db.objectStoreNames.contains(STORE_NAME)) {
    // Installation neuve
    const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
    store.createIndex("syncStatus", "syncStatus");
    store.createIndex("createdAt", "createdAt");
    store.createIndex("accountId", "accountId");
    return; // Rien à migrer : aucune photo n'existe encore.
  }

  const store = transaction.objectStore(STORE_NAME) as UpgradeStore;

  // v1 → v2 : l'index `accountId` apparaît.
  if (oldVersion < 2 && !store.indexNames.contains("accountId")) {
    store.createIndex("accountId", "accountId");
  }

  // v2 → v3 : les photos existantes rejoignent un produit.
  if (oldVersion < 3) await backfillProductIds(store);

  // v3 → v4 : le regroupement s'appelle désormais `productId`.
  if (oldVersion < 4) await renameProductKey(store);

  // v4 → v5 : le tri avant envoi disparaît, et la coche avec lui.
  if (oldVersion < 5) await dropSelectionFlag(store);
};

class PhotoStorageService {
  private dbPromise: Promise<IDBPDatabase>;

  constructor() {
    this.dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade: upgradePhotoStore,
    });
  }

  /**
   * Saves a photo to IndexedDB
   */
  async savePhoto(photo: Photo): Promise<void> {
    try {
      const db = await this.dbPromise;
      await db.put(STORE_NAME, photo);
    } catch (error) {
      throw new Error(`Échec de l'enregistrement de la photo : ${messageOf(error)}`);
    }
  }

  /**
   * Gets all photos from IndexedDB
   */
  async getAllPhotos(): Promise<Photo[]> {
    try {
      const db = await this.dbPromise;
      return await db.getAll(STORE_NAME);
    } catch (error) {
      throw new Error(`Échec de la lecture des photos : ${messageOf(error)}`);
    }
  }

  /**
   * Gets photos by sync status
   */
  async getPhotosByStatus(status: SyncStatus): Promise<Photo[]> {
    try {
      const db = await this.dbPromise;
      return await db.getAllFromIndex(STORE_NAME, "syncStatus", status);
    } catch (error) {
      throw new Error(`Échec de la lecture des photos par statut : ${messageOf(error)}`);
    }
  }

  /**
   * Gets photos for a specific account
   */
  async getPhotosByAccount(accountId: string): Promise<Photo[]> {
    try {
      const db = await this.dbPromise;
      return await db.getAllFromIndex(STORE_NAME, "accountId", accountId);
    } catch (error) {
      throw new Error(`Échec de la lecture des photos du compte : ${messageOf(error)}`);
    }
  }

  /**
   * Gets photos by account and status
   */
  async getPhotosByAccountAndStatus(
    accountId: string,
    status: SyncStatus
  ): Promise<Photo[]> {
    try {
      const db = await this.dbPromise;
      const allPhotos = await db.getAllFromIndex(
        STORE_NAME,
        "accountId",
        accountId
      );
      return allPhotos.filter((photo) => photo.syncStatus === status);
    } catch (error) {
      throw new Error(
        `Échec de la lecture des photos du compte par statut : ${messageOf(error)}`
      );
    }
  }

  /**
   * Updates a photo's sync status, and the cause of its last failure
   */
  async updateSyncStatus(
    id: string,
    status: SyncStatus,
    lastError?: string
  ): Promise<void> {
    try {
      const db = await this.dbPromise;
      const photo = await db.get(STORE_NAME, id);
      if (photo) {
        photo.syncStatus = status;
        photo.lastError = lastError; // effacée dès que l'envoi repart
        await db.put(STORE_NAME, photo);
      }
    } catch (error) {
      throw new Error(`Échec de la mise à jour du statut d'envoi : ${messageOf(error)}`);
    }
  }

  /**
   * Deletes a photo from IndexedDB
   */
  async deletePhoto(id: string): Promise<void> {
    try {
      const db = await this.dbPromise;
      await db.delete(STORE_NAME, id);
    } catch (error) {
      throw new Error(`Échec de la suppression de la photo : ${messageOf(error)}`);
    }
  }

  /**
   * Marks captured photos as pending for upload
   */
  async markPhotosForUpload(photoIds: string[]): Promise<void> {
    try {
      const db = await this.dbPromise;
      for (const id of photoIds) {
        const photo = await db.get(STORE_NAME, id);
        if (photo && photo.syncStatus === SyncStatus.CAPTURED) {
          photo.syncStatus = SyncStatus.PENDING;
          await db.put(STORE_NAME, photo);
        }
      }
    } catch (error) {
      throw new Error(`Échec du marquage des photos pour l'envoi : ${messageOf(error)}`);
    }
  }

  /**
   * Clears all photos (e.g., after successful batch upload)
   */
  async clearAll(): Promise<void> {
    try {
      const db = await this.dbPromise;
      await db.clear(STORE_NAME);
    } catch (error) {
      throw new Error(`Échec de l'effacement des photos : ${messageOf(error)}`);
    }
  }

  /**
   * Retire de l'appareil les produits dont l'envoi est vérifié et dont la
   * capture remonte à plus de sept jours. Sans geste de l'opérateur.
   *
   * Ce qui n'est pas parti reste, aussi longtemps qu'il le faudra : c'est
   * `purgeableProducts` qui tient la règle, et elle prime sur l'âge.
   *
   * Ce qu'elle retire est journalisé — c'est la seule suppression que
   * l'opérateur ne déclenche pas, elle doit donc laisser une trace, comme tout
   * ce qui quitte le téléphone. Le journal est dans un `finally` : une purge
   * interrompue a déjà supprimé des photos, et c'est justement le cas où le
   * silence coûterait le plus cher.
   *
   * @returns Le nombre de photos retirées.
   */
  async purgeExpiredProducts(accountId: string): Promise<number> {
    const photos = await this.getPhotosByAccount(accountId);
    const products = purgeableProducts(photos);
    if (products.length === 0) return 0;

    const toPurge = products.flatMap((product) => product.photos);
    let purged = 0;
    try {
      for (const photo of toPurge) {
        await this.deletePhoto(photo.id);
        purged++;
      }
      return purged;
    } finally {
      console.info(
        `Rétention ${RETENTION_DAYS} jours : ${purged}/${toPurge.length} photo(s) retirée(s) —`,
        products.map((product) => product.id).join(", ")
      );
    }
  }
}

export const photoStorageService = new PhotoStorageService();
